import { inject, Injectable } from '@angular/core';
import { ITelemetryProvider } from './telemetry-provider.interface';
import { ApplicationInsightsTelemetryProvider } from './application-insights-telemetry.provider';

/**
 * Telemetry service that delegates to a concrete provider implementation.
 * This allows for easy swapping between different observability platforms
 * (Application Insights, Datadog, New Relic, etc.) without changing consuming code.
 */
@Injectable({
  providedIn: 'root',
})
export class TelemetryService implements ITelemetryProvider {
  private readonly provider: ITelemetryProvider;

  constructor() {
    // In the future, this could be configured via environment or feature flags
    // e.g., if (environment.telemetryProvider === 'datadog') { ... }
    this.provider = inject(ApplicationInsightsTelemetryProvider);
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    this.provider.trackEvent(name, properties);
  }

  trackPageView(name?: string, uri?: string): void {
    this.provider.trackPageView(name, uri);
  }

  trackException(error: Error, severityLevel?: number): void {
    this.provider.trackException(error, severityLevel);
  }

  trackMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    this.provider.trackMetric(name, average, properties);
  }

  trackTrace(message: string, properties?: Record<string, unknown>): void {
    this.provider.trackTrace(message, properties);
  }

  setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string): void {
    this.provider.setAuthenticatedUserContext(authenticatedUserId, accountId);
  }

  clearAuthenticatedUserContext(): void {
    this.provider.clearAuthenticatedUserContext();
  }

  setCustomProperty(key: string, value: string): void {
    this.provider.setCustomProperty(key, value);
  }

  removeCustomProperty(key: string): void {
    this.provider.removeCustomProperty(key);
  }

  clearCustomProperties(): void {
    this.provider.clearCustomProperties();
  }

  flush(): void {
    this.provider.flush();
  }
}
